/**
 * Sticky Final review rail — confirmation of what will be recorded, always on
 * screen on wide viewports. Create is pinned in the card footer; the review
 * body scrolls inside the card when the viewport is short.
 *
 * On narrow screens the same card sits in document flow after the form fields
 * (so Review is still the last thing you read), and Create lives in the sticky
 * bottom action bar instead of the card footer.
 *
 * `aria-live="off"` is deliberate — announcing every keystroke would make the
 * form unusable with a screen reader. The error summary is the only live region.
 */

import type { ReactNode } from 'react'
import { Button, Card } from '@w7/ui'
import { ROLE_LABEL } from '@/data/types'
import { ReviewSection, type SectionCtx } from './sections'

export type ChecklistState = 'done' | 'todo' | 'error'

export interface ChecklistEntry {
  id: string
  label: string
  state: ChecklistState
}

export interface CreateSubmitProps {
  label: string
  busy: boolean
  /** Why the primary would not go through, in plain words. Never a disabled button. */
  blockReason: string | null
  /** Defaults to plus (create). Edit uses check. */
  icon?: 'plus' | 'check'
}

/**
 * The primary action. On wide screens it lives in the sticky rail footer; on
 * narrow screens in the bottom action bar. Never disabled — incomplete forms
 * answer every field and focus the first invalid control instead.
 */
export function CreateSubmit({ label, busy, blockReason, icon = 'plus' }: CreateSubmitProps) {
  return (
    <div className="w7-create-submit">
      <Button type="submit" variant="primary" icon={icon} busy={busy}>
        {label}
      </Button>
      {blockReason ? <p className="w7-create-block">{blockReason}</p> : null}
    </div>
  )
}

export interface SummaryRailProps {
  ctx: SectionCtx
  checklist: ChecklistEntry[]
  onNavigate: (sectionId: string) => void
  submit: CreateSubmitProps
  banner?: ReactNode
  ready?: boolean
}

export function SummaryRail({
  ctx,
  checklist,
  onNavigate,
  submit,
  banner,
  ready = false,
}: SummaryRailProps) {
  const { role, values } = ctx
  const username = values.username.trim()
  const doneCount = checklist.filter((entry) => entry.state === 'done').length
  const errorCount = checklist.filter((entry) => entry.state === 'error').length
  const subtitle = username
    ? `${ROLE_LABEL[role]} · ${username}`
    : `${ROLE_LABEL[role]} · live as you type`

  return (
    <aside id="review" className="w7-create-rail" aria-label="Final review" aria-live="off">
      <Card
        className="w7-create-railcard"
        title="Final review"
        subtitle={subtitle}
        footer={
          <div className="w7-create-railfoot">
            <div className="w7-create-railprogress" aria-label="Form progress">
              <span className="w7-create-railprogress-dots" aria-hidden="true">
                {checklist.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    className="w7-create-railprogress-dot"
                    data-state={entry.state}
                    title={entry.label}
                    onClick={() => onNavigate(entry.id)}
                  />
                ))}
              </span>
              <span className="w7-create-railprogress-label">
                {errorCount > 0
                  ? `${errorCount} need${errorCount === 1 ? 's' : ''} fixing`
                  : `${doneCount} of ${checklist.length} ready`}
              </span>
            </div>
            <div className="w7-create-railsubmit">
              <CreateSubmit {...submit} />
            </div>
          </div>
        }
      >
        <ReviewSection ctx={ctx} banner={banner} ready={ready} />
      </Card>
    </aside>
  )
}
